import type { Club, SubscriptionTier } from '@court-sync/shared';
import {
  buildClubAbsoluteUrl,
  buildClubLoginPath,
  buildClubPath,
  type ClubPathFields,
} from '@/lib/clubPaths';
import { getClubHostMode } from '@/lib/clubHost';
import { useClubSlug } from './useClubSlug';
import { useSubscription } from './useSubscription';

type ClubPathSource = Club | ClubPathFields | null | undefined;

function toPathFields(
  club: ClubPathSource,
  slug: string | undefined,
  orgTier?: SubscriptionTier
): ClubPathFields | null {
  if (club) {
    return {
      slug: club.slug,
      subdomain: club.subdomain,
      subdomainEnabled: club.subdomainEnabled,
      orgTier,
    };
  }
  if (slug) return { slug, orgTier };
  return null;
}

/**
 * Subdomain-aware club path helpers for public club pages.
 */
export function useClubPaths(club?: ClubPathSource) {
  const slug = useClubSlug();
  const hostMode = getClubHostMode();
  const { currentTier } = useSubscription();
  const fields = toPathFields(club, slug, currentTier as SubscriptionTier);

  if (!fields) {
    return {
      slug,
      hostMode,
      fields: null as ClubPathFields | null,
      base: null as string | null,
      p: (_subpath: string) => null as string | null,
      login: (_returnPath?: string) => null as string | null,
      absolute: (_subpath?: string) => null as string | null,
    };
  }

  const base = buildClubPath(fields, '', hostMode) || '/';
  const p = (subpath: string) => buildClubPath(fields, subpath, hostMode);
  const login = (returnPath?: string) => buildClubLoginPath(fields, returnPath, hostMode);
  const absolute = (subpath = '') => buildClubAbsoluteUrl(fields, subpath, undefined, hostMode);

  return {
    slug: slug ?? fields.slug,
    hostMode,
    fields,
    base,
    p,
    login,
    absolute,
  };
}
